import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../i18n/I18nContext';
import { UserPlus, CheckCircle, Layers, Lock, Mail, RefreshCw } from 'lucide-react';
import Footer from '../components/Footer';
import Logo from '../components/Logo';
import '../styles/Auth.css';

const RESEND_COOLDOWN = 60;

const Register = () => {
  const navigate = useNavigate();
  const { register, resendVerification } = useAuth();
  const { t, lang } = useI18n();
  const isTr = lang === 'tr';

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Verification pending state
  const [verificationSent, setVerificationSent] = useState(false);
  const [sentEmail, setSentEmail] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resending, setResending] = useState(false);
  const [resendError, setResendError] = useState('');
  const cooldownRef = useRef(null);

  useEffect(() => {
    return () => { if (cooldownRef.current) clearInterval(cooldownRef.current); };
  }, []);

  const startCooldown = () => {
    setResendCooldown(RESEND_COOLDOWN);
    cooldownRef.current = setInterval(() => {
      setResendCooldown((s) => {
        if (s <= 1) { clearInterval(cooldownRef.current); return 0; }
        return s - 1;
      });
    }, 1000);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError(t('auth.register.passwordsDoNotMatch'));
      setLoading(false);
      return;
    }

    const { confirmPassword, ...userData } = formData;
    const result = await register(userData);

    if (result.success) {
      if (result.verificationPending) {
        setSentEmail(formData.email);
        setVerificationSent(true);
        startCooldown();
      } else {
        navigate('/dashboard');
      }
    } else {
      setError(result.error);
    }

    setLoading(false);
  };

  const handleResend = async () => {
    if (resendCooldown > 0 || resending) return;
    setResending(true);
    setResendError('');
    const result = await resendVerification(sentEmail);
    setResending(false);
    if (result.success) {
      startCooldown();
    } else {
      setResendError(result.error || t('auth.login.resendFailed'));
    }
  };

  // ─── Verification pending screen ────────────────────────────────────────────
  if (verificationSent) {
    return (
      <div className="auth-split">
        <aside className="auth-split-left">
          <div className="auth-split-left-inner">
            <div className="auth-split-logo">
              <Logo height={52} />
            </div>
            <h2 className="auth-split-headline">
              {isTr ? 'Transkripsiyon artık bu kadar kolay.' : 'Transcription, made effortless.'}
            </h2>
            <p className="auth-split-sub">
              {isTr
                ? 'Video ve ses içeriklerinizi %99 doğrulukla, saniyeler içinde metne dönüştürün.'
                : 'Turn audio and video into accurate text in seconds — right on your desktop.'}
            </p>
            <ul className="auth-split-feats">
              <li><CheckCircle size={14} />{isTr ? '%99 doğruluk oranı' : '99% accuracy rate'}</li>
              <li><Layers size={14} />{isTr ? '50+ dil desteği' : '50+ languages supported'}</li>
              <li><Lock size={14} />{isTr ? '%100 gizlilik — veriler cihazınızda' : '100% private — data stays local'}</li>
            </ul>
          </div>
          <div className="auth-split-grid" aria-hidden="true" />
        </aside>

        <div className="auth-split-right">
          <div className="auth-split-form">
            <div className="auth-logo">
              <Logo height={40} />
            </div>

            <div className="auth-verify-icon">
              <Mail size={40} strokeWidth={1.5} />
            </div>

            <h1>{t('auth.register.checkEmail')}</h1>
            <p className="auth-subtitle">
              {t('auth.register.verificationSent')}{' '}
              <strong style={{ color: '#f0f0f2' }}>{sentEmail}</strong>
            </p>
            <p className="auth-subtitle" style={{ marginTop: 8 }}>
              {t('auth.register.verificationInstruction')}
            </p>

            {resendError && (
              <p style={{ color: '#f87171', fontSize: '0.82rem', marginTop: 16, marginBottom: 0 }}>{resendError}</p>
            )}

            <button
              className="btn-primary-auth"
              onClick={handleResend}
              disabled={resendCooldown > 0 || resending}
              style={{ marginTop: 24 }}
            >
              <RefreshCw size={16} />
              {resendCooldown > 0
                ? t('auth.register.resendIn', { seconds: resendCooldown })
                : t('auth.register.resendLink')}
            </button>

            <p className="auth-footer" style={{ marginTop: 20 }}>
              <Link to="/login">{t('auth.register.backToLogin')}</Link>
            </p>
          </div>
          <Footer />
        </div>
      </div>
    );
  }

  // ─── Normal registration form ────────────────────────────────────────────────
  return (
    <div className="auth-split">

      {/* ── Left panel — brand ── */}
      <aside className="auth-split-left">
        <div className="auth-split-left-inner">
          <div className="auth-split-logo">
            <Logo height={52} />
          </div>

          <h2 className="auth-split-headline">
            {isTr
              ? 'Transkripsiyon artık bu kadar kolay.'
              : 'Transcription, made effortless.'}
          </h2>

          <p className="auth-split-sub">
            {isTr
              ? 'Video ve ses içeriklerinizi %99 doğrulukla, saniyeler içinde metne dönüştürün.'
              : 'Turn audio and video into accurate text in seconds — right on your desktop.'}
          </p>

          <ul className="auth-split-feats">
            <li><CheckCircle size={14} />{isTr ? '%99 doğruluk oranı' : '99% accuracy rate'}</li>
            <li><Layers size={14} />{isTr ? '50+ dil desteği' : '50+ languages supported'}</li>
            <li><Lock size={14} />{isTr ? '%100 gizlilik — veriler cihazınızda' : '100% private — data stays local'}</li>
          </ul>
        </div>

        {/* decorative background */}
        <div className="auth-split-grid" aria-hidden="true" />
      </aside>

      {/* ── Right panel — form ── */}
      <div className="auth-split-right">
        <div className="auth-split-form">

          <div className="auth-logo">
            <Logo height={40} />
          </div>

          <h1>{t('auth.register.title')}</h1>
          <p className="auth-subtitle">{t('auth.register.subtitle')}</p>

          {error && <div className="error-message">{error}</div>}

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="name">{t('auth.register.fullName')}</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                placeholder={t('auth.register.fullNamePlaceholder')}
                autoComplete="name"
              />
            </div>

            <div className="form-group">
              <label htmlFor="email">{t('auth.fields.email')}</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                placeholder={t('auth.fields.emailPlaceholder')}
                autoComplete="email"
              />
            </div>

            <div className="form-group">
              <label htmlFor="password">{t('auth.fields.password')}</label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                required
                placeholder={t('auth.register.createPasswordPlaceholder')}
                minLength="8"
                autoComplete="new-password"
              />
              <small>{t('auth.register.passwordHint')}</small>
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">{t('auth.register.confirmPassword')}</label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                required
                placeholder={t('auth.register.confirmPasswordPlaceholder')}
                autoComplete="new-password"
              />
            </div>

            {/* KVKK + Terms consent — required for KVKK compliance */}
            <div className="auth-consent">
              <label className="auth-consent-row">
                <input type="checkbox" required className="auth-consent-check" />
                <span>
                  {isTr ? (
                    <><Link to="/kvkk">KVKK Aydınlatma Metni</Link>'ni okudum, kişisel verilerimin işlenmesine açık rıza veriyorum.</>
                  ) : (
                    <>I have read the <Link to="/kvkk">KVKK Privacy Notice</Link> and consent to processing of my personal data.</>
                  )}
                </span>
              </label>
              <label className="auth-consent-row">
                <input type="checkbox" required className="auth-consent-check" />
                <span>
                  {isTr ? (
                    <><Link to="/kullanim-kosullari">Kullanım Koşulları</Link>'nı ve{' '}
                    <Link to="/gizlilik-politikasi">Gizlilik Politikası</Link>'nı okudum, kabul ediyorum.
                    18 yaşından büyük olduğumu beyan ederim.</>
                  ) : (
                    <>I agree to the <Link to="/kullanim-kosullari">Terms of Service</Link> and{' '}
                    <Link to="/gizlilik-politikasi">Privacy Policy</Link>.
                    I confirm I am 18 or older.</>
                  )}
                </span>
              </label>
            </div>

            <button type="submit" className="btn-primary-auth" disabled={loading}>
              {loading ? t('auth.register.creatingAccount') : (
                <><UserPlus size={16} />{t('auth.register.createAccount')}</>
              )}
            </button>
          </form>

          <p className="auth-footer">
            {t('auth.register.alreadyAccount')}{' '}
            <Link to="/login">{t('auth.register.signInHere')}</Link>
          </p>
        </div>

        <Footer />
      </div>
    </div>
  );
};

export default Register;
